import {
  Header as CarbonHeader,
  HeaderMenuItem as CarbonHeaderMenuItem,
  HeaderNavigation as CarbonHeaderNavigation,
} from "@carbon/react";
import { FileText, Import, Settings, User, Users } from "lucide-react";
import { CanAccess, useTranslate, useUserMenu } from "ra-core";
import { Link, matchPath, useLocation, useNavigate } from "react-router";
import { RefreshButton } from "@/components/admin/refresh-button";
import { ThemeModeToggle } from "@/components/admin/theme-mode-toggle";
import { UserMenu } from "@/components/admin/user-menu";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";
import { ImportPage } from "../misc/ImportPage";
import { ChangelogPage } from "../misc/ChangelogPage";
import { GlobalSearchButton } from "../search/GlobalSearchButton";

const Header = () => {
  const { darkModeLogo, lightModeLogo, title } = useConfigurationContext();
  const location = useLocation();
  const navigate = useNavigate();

  let currentPath: string | boolean = "/";
  if (matchPath("/", location.pathname)) {
    currentPath = "/";
  } else if (matchPath("/leads/*", location.pathname)) {
    currentPath = "/leads";
  } else if (matchPath("/contacts/*", location.pathname)) {
    currentPath = "/contacts";
  } else if (matchPath("/companies/*", location.pathname)) {
    currentPath = "/companies";
  } else if (matchPath("/deals/*", location.pathname)) {
    currentPath = "/deals";
  } else if (matchPath("/sales/*", location.pathname)) {
    currentPath = "/sales";
  } else {
    currentPath = false;
  }

  const translate = useTranslate();
  const navigationLabel = (key: string, fallback: string) => {
    const value = translate(key, { smart_count: 2 });
    return value === key ? fallback : value;
  };

  return (
    <>
      <CarbonHeader
        aria-label="Orbitas CRM"
        className="orbitas-carbon-nav orbitas-carbon-header grow"
      >
        <div className="orbitas-carbon-header__inner px-4">
            <div className="orbitas-carbon-header__row flex justify-between items-center flex-1">
              <Link
                to="/"
                className="orbitas-carbon-brand flex items-center gap-2 text-secondary-foreground no-underline"
              >
                <img
                  className="[.light_&]:hidden h-6"
                  src={darkModeLogo}
                  alt=""
                />
                <img
                  className="[.dark_&]:hidden h-6"
                  src={lightModeLogo}
                  alt=""
                />
                <h1 className="orbitas-carbon-brand__title text-xl font-semibold">
                  {title}
                </h1>
              </Link>
              <div className="orbitas-carbon-navigation">
                <CarbonHeaderNavigation
                  aria-label="CRM sections"
                  className="orbitas-carbon-tabs"
                >
                  <NavigationTab
                    label={navigationLabel("ra.page.dashboard", "Dashboard")}
                    to="/"
                    isActive={currentPath === "/"}
                    onNavigate={navigate}
                  />
                  <NavigationTab
                    label={navigationLabel("resources.leads.name", "Leads")}
                    to="/leads"
                    isActive={currentPath === "/leads"}
                    onNavigate={navigate}
                  />
                  <NavigationTab
                    label={navigationLabel("resources.contacts.name", "Contacts")}
                    to="/contacts"
                    isActive={currentPath === "/contacts"}
                    onNavigate={navigate}
                  />
                  <NavigationTab
                    label={navigationLabel("resources.companies.name", "Companies")}
                    to="/companies"
                    isActive={currentPath === "/companies"}
                    onNavigate={navigate}
                  />
                  <NavigationTab
                    label={navigationLabel("resources.deals.name", "Deals")}
                    to="/deals"
                    isActive={currentPath === "/deals"}
                    onNavigate={navigate}
                  />
                  <CanAccess resource="sales" action="list">
                    <NavigationTab
                      label={navigationLabel("resources.sales.name", "Sales team")}
                      to="/sales"
                      isActive={currentPath === "/sales"}
                      onNavigate={navigate}
                    />
                  </CanAccess>
                </CarbonHeaderNavigation>
              </div>
              <div className="orbitas-carbon-tools flex items-center">
                <GlobalSearchButton />
                <ThemeModeToggle />
                <RefreshButton />
                <UserMenu>
                  <ProfileMenu />
                  <CanAccess resource="sales" action="list">
                    <UsersMenu />
                  </CanAccess>
                  <CanAccess resource="configuration" action="edit">
                    <SettingsMenu />
                  </CanAccess>
                  <ImportFromJsonMenuItem />
                  <ChangelogMenuItem />
                </UserMenu>
              </div>
            </div>
        </div>
      </CarbonHeader>
    </>
  );
};

const NavigationTab = ({
  label,
  to,
  isActive,
  onNavigate,
}: {
  label: string;
  to: string;
  isActive: boolean;
  onNavigate: (to: string) => void;
}) => (
  <CarbonHeaderMenuItem
    href={to}
    className={`orbitas-carbon-tab px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
      isActive
        ? "text-secondary-foreground border-secondary-foreground"
        : "text-secondary-foreground/70 border-transparent hover:text-secondary-foreground/80"
    }`}
    aria-current={isActive ? "page" : undefined}
    onClick={(event) => {
      if (
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey
      ) {
        event.preventDefault();
        onNavigate(to);
      }
    }}
  >
    {label}
  </CarbonHeaderMenuItem>
);

const UsersMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<UsersMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/sales" className="flex items-center gap-2">
        <Users />
        {translate("resources.sales.name", { smart_count: 2 })}
      </Link>
    </DropdownMenuItem>
  );
};

const ProfileMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ProfileMenu> must be used inside <UserMenu?");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/profile" className="flex items-center gap-2">
        <User />
        {translate("crm.profile.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const SettingsMenu = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<SettingsMenu> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to="/settings" className="flex items-center gap-2">
        <Settings />
        {translate("crm.settings.title")}
      </Link>
    </DropdownMenuItem>
  );
};

const ImportFromJsonMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ImportFromJsonMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ImportPage.path} className="flex items-center gap-2">
        <Import />
        {translate("crm.header.import_data")}
      </Link>
    </DropdownMenuItem>
  );
};

const ChangelogMenuItem = () => {
  const translate = useTranslate();
  const userMenuContext = useUserMenu();
  if (!userMenuContext) {
    throw new Error("<ChangelogMenuItem> must be used inside <UserMenu>");
  }
  return (
    <DropdownMenuItem asChild onClick={userMenuContext.onClose}>
      <Link to={ChangelogPage.path} className="flex items-center gap-2">
        <FileText />
        {translate("crm.changelog.title")}
      </Link>
    </DropdownMenuItem>
  );
};
export default Header;
